import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import hbAnalyticsApi from "../../api/hbAnalyticsApi";
import responsesApi from "../../api/responsesApi";
import companiesApi from "../../api/companiesApi";

import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import workScheme from "../../data/workScheme";
import biologicalSexList from "../../data/biologicalSex";
import jobArea from "../../data/jobArea";

import styles from "./HraAnalyticsPage.module.css";

const pickFirst = (...values) =>
  values.find((v) => v !== undefined && v !== null);

const asSeries = (value) => (Array.isArray(value) ? value : []);

const normalizeHraAnalytics = (raw) => {
  const source = pickFirst(
    raw?.hra,
    raw?.data?.hra,
    raw?.data?.analytics,
    raw?.data?.hraAnalytics,
    raw?.analytics,
    raw?.hraAnalytics,
    raw?.data,
    raw
  );

  return {
    totalRespondents: pickFirst(raw?.totalRespondents, source?.totalRespondents),
    metabolicRisk: asSeries(source?.metabolicRisk),
    nutritionalState: asSeries(source?.nutritionalState),
    lifeStyle: asSeries(source?.lifeStyle),
    physicalActivityEvaluation: asSeries(source?.physicalActivityEvaluation),
    workHappiness: asSeries(source?.workHappiness),
    overallEvaluation: asSeries(source?.overallEvaluation)
  };
};

const normalizeStressAnalytics = (raw) => {
  const source = pickFirst(
    raw?.stress,
    raw?.data?.stress,
    raw?.data?.analytics,
    raw?.data?.stressAnalytics,
    raw?.analytics,
    raw?.stressAnalytics,
    raw?.data,
    raw
  );

  return {
    totalRespondents: pickFirst(raw?.totalRespondents, source?.totalRespondents),
    hasBurnOut: asSeries(source?.hasBurnOut),
    exhaustionLevel: asSeries(source?.exhaustionLevel),
    cynicismLevel: asSeries(source?.cynicismLevel),
    efficacyLevel: asSeries(source?.efficacyLevel),
    physicalStress: asSeries(source?.physicalStress)
  };
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const downloadBlob = (blobData, filename) => {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const HbAnalyticsGlobalPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    ageRange: searchParams.get("ageRange") || "",
    residenceState: searchParams.get("residenceState") || "",
    workScheme: searchParams.get("workScheme") || "",
    biologicalSex: searchParams.get("biologicalSex") || "",
    jobArea: searchParams.get("jobArea") || "",
    companyId: searchParams.get("companyId") || ""
  });

  const [companies, setCompanies] = useState([]);
  const [hraData, setHraData] = useState(null);
  const [stressData, setStressData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [responsesList, setResponsesList] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [exporting, setExporting] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);

    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(name, value);
    else nextParams.delete(name);
    setSearchParams(nextParams);

    setCurrentPage(1);
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await companiesApi.getCompanies();
        setCompanies(list);
      } catch (error) {
        console.error("Error cargando empresas:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [hraRaw, stressRaw] = await Promise.all([
          hbAnalyticsApi.getHbHraGlobalAnalytics(filters),
          hbAnalyticsApi.getHbStressGlobalAnalytics(filters)
        ]);

        const normalizedHra = normalizeHraAnalytics(hraRaw);
        const normalizedStress = normalizeStressAnalytics(stressRaw);

        if (initialTotal === null) {
          setInitialTotal(
            normalizedHra?.totalRespondents ?? normalizedStress?.totalRespondents ?? 0
          );
        }

        setHraData(normalizedHra);
        setStressData(normalizedStress);
      } catch (error) {
        console.error("Error cargando analíticas globales HB:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      setLoadingResponses(true);
      try {
        const list = await hbAnalyticsApi.getHbGlobalRespondants({
          companyId: filters.companyId || undefined
        });
        setResponsesList(list || []);
      } catch (error) {
        console.error("Error cargando respondants globales HB:", error);
      } finally {
        setLoadingResponses(false);
      }
    })();
  }, [filters.companyId]);

  const sortedRespondents = useMemo(() => {
    return [...responsesList].sort((a, b) => {
      const va = (a[empSortBy] ?? a.username ?? a.email ?? "").toString();
      const vb = (b[empSortBy] ?? b.username ?? b.email ?? "").toString();
      if (va < vb) return empSortOrder === "asc" ? -1 : 1;
      if (va > vb) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [responsesList, empSortBy, empSortOrder]);

  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setEmpSortBy(column);
    setEmpSortOrder("asc");
  };

  const handleDelete = async (responseId) => {
    try {
      await responsesApi.deleteHBResponse(responseId);
      setResponsesList((prev) => prev.filter((r) => r.responseId !== responseId));
    } catch (error) {
      console.error("Error eliminando respuesta HB:", error);
      alert("No se pudo eliminar la respuesta.");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await responsesApi.exportHBResponses(filters.companyId || undefined);
      const suffix = filters.companyId ? filters.companyId : "all";
      downloadBlob(blob, `hb_responses_${suffix}.xlsx`);
    } catch (error) {
      console.error("Error exportando respuestas HB:", error);
    } finally {
      setExporting(false);
    }
  };

  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>{label}</label>
      <select
        id={name}
        name={name}
        value={filters[name]}
        onChange={handleFilterChange}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const totalRespondents =
    hraData?.totalRespondents ?? stressData?.totalRespondents ?? responsesList.length;

  const hasHraCharts =
    (hraData?.metabolicRisk?.length || 0) > 0 ||
    (hraData?.nutritionalState?.length || 0) > 0 ||
    (hraData?.lifeStyle?.length || 0) > 0 ||
    (hraData?.physicalActivityEvaluation?.length || 0) > 0 ||
    (hraData?.workHappiness?.length || 0) > 0 ||
    (hraData?.overallEvaluation?.length || 0) > 0;

  const hasStressCharts =
    (stressData?.hasBurnOut?.length || 0) > 0 ||
    (stressData?.exhaustionLevel?.length || 0) > 0 ||
    (stressData?.cynicismLevel?.length || 0) > 0 ||
    (stressData?.efficacyLevel?.length || 0) > 0 ||
    (stressData?.physicalStress?.length || 0) > 0;

  return (
    <div className={styles.page}>
      {initialTotal !== null && (
        <div className={styles.initialTotalCapsule}>
          Total inicial: <span>{initialTotal}</span>
        </div>
      )}

      <header className={styles.header}>
        <h2>Gráficas Globales HRA & Burnout</h2>
        <p className={styles.subheader}>Global overview across all companies</p>
      </header>

      <section className={styles.filters}>
        {renderFilter("Rango de Edad", "ageRange", ageRanges)}
        {renderFilter("Estado de Residencia", "residenceState", mexicoStates)}
        {renderFilter("Esquema Laboral", "workScheme", workScheme)}
        {renderFilter("Sexo Biológico", "biologicalSex", biologicalSexList)}
        {renderFilter("Área de Trabajo", "jobArea", jobArea)}

        <div className={styles.filterItem}>
          <label htmlFor="companyId" className={styles.filterLabel}>Empresa</label>
          <select
            id="companyId"
            name="companyId"
            value={filters.companyId}
            onChange={handleFilterChange}
            className={styles.select}
          >
            <option value="">Todas</option>
            {companies.map((company) => (
              <option key={company._id} value={company._id}>{company.name}</option>
            ))}
          </select>
        </div>
      </section>

      {!loading && (
        <div className={styles.totalCapsule}>
          Total encuestados: <span>{totalRespondents}</span>
        </div>
      )}

      <section className={styles.content}>
        {loading ? (
          <p className={styles.message}>Cargando gráficas globales...</p>
        ) : !hasHraCharts && !hasStressCharts ? (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        ) : (
          <>
            {hasHraCharts && (
              <>
                <h3 style={{ marginBottom: "0.75rem" }}>Métricas HRA</h3>
                <div className={styles.charts}>
                  <BarChartComponent data={hraData.metabolicRisk} title="Riesgo Metabólico" />
                  <BarChartComponent data={hraData.nutritionalState} title="Estado Nutricional" />
                  <BarChartComponent data={hraData.lifeStyle} title="Estilo de Vida" />
                  <BarChartComponent data={hraData.physicalActivityEvaluation} title="Actividad Física" />
                  <BarChartComponent data={hraData.workHappiness} title="Felicidad Laboral" />
                  <BarChartComponent data={hraData.overallEvaluation} title="Evaluación General" />
                </div>
              </>
            )}

            {hasStressCharts && (
              <>
                <h3 style={{ margin: "1.25rem 0 0.75rem" }}>Métricas Estrés y Burnout</h3>
                <div className={styles.charts}>
                  <BarChartComponent data={stressData.hasBurnOut} title="Presencia de Burnout" />
                  <BarChartComponent data={stressData.exhaustionLevel} title="Agotamiento Emocional" />
                  <BarChartComponent data={stressData.cynicismLevel} title="Desconexión Laboral" />
                  <BarChartComponent data={stressData.efficacyLevel} title="Eficiencia Laboral" />
                  <BarChartComponent data={stressData.physicalStress} title="Estrés Físico" />
                </div>
              </>
            )}
          </>
        )}
      </section>

      <hr className={styles.separator} />

      <section className={styles.responsesSection}>
        <h3>Listado de Empleados</h3>

        {loadingResponses ? (
          <p className={styles.message}>Cargando respuestas...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th className={styles.clickable} onClick={() => onSort("email")}>Correo</th>
                <th className={styles.clickable} onClick={() => onSort("questionnaireName")}>Cuestionario</th>
                <th className={styles.clickable} onClick={() => onSort("timestamp")}>Fecha</th>
                <th className={styles.clickable} onClick={() => onSort("status")}>Estado</th>
                <th>Borrar</th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.length === 0 ? (
                <tr>
                  <td colSpan={6} className={styles.message}>No hay empleados para mostrar.</td>
                </tr>
              ) : (
                displayedRespondents.map((r, i) => (
                  <tr key={r.responseId || `${r.email || r.username}-${i}`}>
                    <td>{(currentPage - 1) * pageSize + i + 1}</td>
                    <td>{r.email || r.username || "-"}</td>
                    <td>{r.questionnaireName || "healthBurnout"}</td>
                    <td>{formatDate(r.timestamp)}</td>
                    <td>{r.status || "Respondido"}</td>
                    <td>
                      <button
                        className={styles.deleteButton}
                        disabled={!r.responseId}
                        onClick={() => handleDelete(r.responseId)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Anterior
          </button>
          <span className={styles.pageInfo}>Página {currentPage} de {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Siguiente
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className={styles.paginationButton}
          >
            {exporting ? "Exportando..." : "Exportar Excel"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default HbAnalyticsGlobalPage;
